import { atom } from 'jotai';
import isEqual from 'lodash/isEqual';

import { ItemDetailRecord } from '../types/inventoryItem';

export type ItemFormState = {
  name: string;
  skuNumber: string;
  categoryId: string | undefined;
  itemGroupId: string | undefined;
  pricingUnitOverride: string | undefined;
  description: string;
  notes: string;
};

const initialState = (): ItemFormState => ({
  name: '',
  skuNumber: '',
  categoryId: undefined,
  itemGroupId: undefined,
  pricingUnitOverride: undefined,
  description: '',
  notes: '',
});

const mapItemToItemForm = (item: ItemDetailRecord): ItemFormState => ({
  name: item.name,
  skuNumber: item.skuNumber,
  categoryId: item.category?.id,
  itemGroupId: item.itemGroup?.id,
  pricingUnitOverride: item.pricingUnitOverride ?? undefined,
  description: item.description ?? '',
  notes: item.notes ?? '',
});

// 🔘🔘🔘 PLAIN ATOMS
export const $itemForm = atom<ItemFormState>(initialState());
export const $discardBaseline = atom<ItemFormState | null>(null);
export const $lastPopulatedItemId = atom<string | undefined>(undefined);
export const $isItemFormPopulated = atom<boolean>(false);

// 🟢🟢🟢 COMPUTED DATA
export const $hasUnsavedChanges = atom((get) => {
  const form = get($itemForm);
  const baseline = get($discardBaseline);

  if (!baseline) return false;

  return !isEqual(form, baseline);
});

export const $canSubmitItemForm = atom((get) => {
  const form = get($itemForm);
  if (!form.name.trim()) return false;
  if (!form.skuNumber.trim()) return false;
  if (!form.categoryId) return false;
  if (!form.itemGroupId) return false;
  return true;
});

// 🟠🟠🟠 ACTION -> UPDATE item form
export const $updateItemForm = atom(
  null,
  (get, set, update: Partial<ItemFormState>) => {
    set($itemForm, { ...get($itemForm), ...update });
  },
);

export const $populateItemFormFromItem = atom(
  null,
  (_get, set, item: ItemDetailRecord) => {
    const formState = mapItemToItemForm(item);
    set($itemForm, formState);
    set($discardBaseline, formState);
    set($lastPopulatedItemId, item.id);
    set($isItemFormPopulated, true);
  },
);

export const $resetItemFormToItem = atom(null, (get, set) => {
  const baseline = get($discardBaseline);
  if (baseline) {
    set($itemForm, baseline);
  }
});

export const $populateItemFormAction = atom(
  null,
  (get, set, item: ItemDetailRecord | null | undefined) => {
    if (!item) return;

    const currentItemId = item.id;
    const lastItemId = get($lastPopulatedItemId);

    if (currentItemId !== lastItemId) {
      set($isItemFormPopulated, false);
    }

    if (!get($isItemFormPopulated)) {
      set($populateItemFormFromItem, item);
    }
  },
);

export const $resetItemForm = atom(null, (_get, set) => {
  set($itemForm, initialState());
  set($discardBaseline, null);
  set($lastPopulatedItemId, undefined);
  set($isItemFormPopulated, false);
});
